import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Battery100Icon, CheckIcon, ChevronDownIcon } from '@heroicons/react/24/outline'
import IkonaModulu from '../ikony/IkonaModulu'
import IkonaFalownika from '../ikony/IkonaFalownika'
import IkonaLadowarki from '../ikony/IkonaLadowarki'
import { jestPolska } from '../../kraj'

/**
 * Wybór kategorii wyszukiwarki w pasku (prośba właściciela 2026-10-12): ikona na początku pola, po kliknięciu lista
 * z modułami, falownikami, magazynami i ładowarkami. Wcześniej szukanie spoza listingu zawsze prowadziło do modułów.
 * Ładowarki tylko w Polsce (w innych krajach nie zbieramy ich ofert, jak w menu „Katalog”).
 */

export const KATEGORIE_SZUKANIA = [
  { id: 'panele', trasa: '/panele', klucz: 'moduly', domyslnie: 'Moduły', Ikona: IkonaModulu },
  { id: 'inwertery', trasa: '/inwertery', klucz: 'falowniki', domyslnie: 'Falowniki', Ikona: IkonaFalownika },
  { id: 'magazyny', trasa: '/magazyny', klucz: 'magazyny', domyslnie: 'Magazyny energii', Ikona: Battery100Icon },
  { id: 'ladowarki', trasa: '/ladowarki', klucz: 'ladowarki', domyslnie: 'Ładowarki', Ikona: IkonaLadowarki, tylkoPolska: true },
]

export const dostepneKategorie = () => KATEGORIE_SZUKANIA.filter((k) => !k.tylkoPolska || jestPolska)

/** Kategoria listingu, na którym stoimy (`/panele?…`, `/magazyny`), albo null. */
export const kategoriaZeSciezki = (sciezka) => dostepneKategorie().find((k) => sciezka === k.trasa || sciezka.startsWith(`${k.trasa}/`)) || null

const KategoriaSzukania = ({ wybrana, onWybierz }) => {
  const { t } = useTranslation('wspolne')
  const [otwarte, setOtwarte] = useState(false)
  const [aktywny, setAktywny] = useState(0)
  const ref = useRef(null)
  const lista = useRef(null)
  const kategorie = dostepneKategorie()
  const obecna = kategorie.find((k) => k.id === wybrana) || kategorie[0]
  const nazwa = (k) => t(`nav.kategoria.${k.klucz}`, { defaultValue: k.domyslnie })

  useEffect(() => {
    if (!otwarte) return undefined
    const poza = (e) => { if (ref.current && !ref.current.contains(e.target)) setOtwarte(false) }
    document.addEventListener('mousedown', poza)
    lista.current?.focus()
    return () => document.removeEventListener('mousedown', poza)
  }, [otwarte])

  const otworz = () => { setAktywny(Math.max(0, kategorie.indexOf(obecna))); setOtwarte(true) }
  const wybierz = (k) => { setOtwarte(false); onWybierz(k.id) }
  const klawisz = (e) => {
    if (e.key === 'Escape') { e.preventDefault(); setOtwarte(false) }
    if (e.key === 'ArrowDown') { e.preventDefault(); setAktywny((i) => Math.min(kategorie.length - 1, i + 1)) }
    if (e.key === 'ArrowUp') { e.preventDefault(); setAktywny((i) => Math.max(0, i - 1)) }
    if (e.key === 'Enter') { e.preventDefault(); wybierz(kategorie[aktywny]) }
  }

  return (
    <div ref={ref} className='relative flex-shrink-0'>
      <button type='button' onClick={() => (otwarte ? setOtwarte(false) : otworz())}
              aria-haspopup='listbox' aria-expanded={otwarte}
              aria-label={t('nav.kategoria.wybor', { defaultValue: 'Szukaj w: {{nazwa}}', nazwa: nazwa(obecna) })}
              title={nazwa(obecna)}
              className='flex h-full items-center gap-1 rounded-l-lg border-r border-gray-200 pl-2.5 pr-1.5 text-gray-600 transition-colors
                         hover:bg-gray-100 hover:text-blue-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500'>
        <obecna.Ikona className='h-5 w-5' />
        <ChevronDownIcon className={`h-3.5 w-3.5 transition-transform ${otwarte ? 'rotate-180' : ''}`} aria-hidden='true' />
      </button>
      {otwarte && (
        <ul ref={lista} role='listbox' tabIndex={-1} onKeyDown={klawisz}
            aria-label={t('nav.kategoria.lista', { defaultValue: 'Kategoria wyszukiwania' })}
            className='absolute left-0 top-full z-40 mt-1.5 w-56 rounded-xl border border-gray-200 bg-white p-1 shadow-lg focus:outline-none'>
          {kategorie.map((k, i) => {
            const zaznaczona = k.id === obecna.id
            return (
              <li key={k.id} role='option' aria-selected={zaznaczona} onMouseEnter={() => setAktywny(i)} onClick={() => wybierz(k)}
                  className={`flex cursor-pointer items-center gap-3 rounded-lg px-2.5 py-2 text-sm ${i === aktywny ? 'bg-blue-50' : ''}`}>
                <span className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg ${zaznaczona ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'}`}>
                  <k.Ikona className='h-5 w-5' />
                </span>
                <span className={`min-w-0 flex-1 ${zaznaczona ? 'font-semibold text-blue-800' : 'text-gray-800'}`}>{nazwa(k)}</span>
                {zaznaczona && <CheckIcon className='h-4 w-4 flex-shrink-0 text-blue-600' strokeWidth={2.5} aria-hidden='true' />}
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

export default KategoriaSzukania
